import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageSquare, X, VideoCamera, CalendarBlank, Lightning, Sparkle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { api } from "../../lib/api";
import { NETWORKS, NETWORK_IDS, TIKTOK_PRIVACY, uploadMedia, errMsg } from "./socialShared";
import { PostPreview } from "./PostPreview";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const empty = { text: "", texts: {}, networks: [], media: [], when: "now", scheduled: "", tiktok_privacy: "PUBLIC_TO_EVERYONE" };

function NetworkPicker({ value, onChange, accounts }) {
  const connected = (prov) => accounts.find((a) => a.provider === prov)?.connected;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {NETWORK_IDS.map((id) => {
        const n = NETWORKS[id]; const on = value.includes(id); const Icon = n.icon;
        return (
          <button type="button" key={id} data-testid={`composer-network-${id}`}
            onClick={() => onChange(on ? value.filter((x) => x !== id) : [...value, id])}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${on ? "border-foreground bg-foreground/5" : "hover:bg-muted"}`}>
            <Icon size={18} weight="fill" style={{ color: n.color }} />
            <span className="font-medium">{n.label}</span>
            {!connected(n.provider) && <span className="ml-auto text-[10px] text-muted-foreground">non relié</span>}
          </button>
        );
      })}
    </div>
  );
}

function MediaPicker({ media, setMedia }) {
  const [progress, setProgress] = useState(null);
  const onFiles = async (files) => {
    for (const f of Array.from(files)) {
      try {
        setProgress(0);
        const m = await uploadMedia(f, setProgress);
        setMedia((prev) => [...prev, m]);
      } catch (e) { toast.error(`${f.name} : ${errMsg(e)}`); }
    }
    setProgress(null);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {media.map((m) => (
          <div key={m.id} className="relative h-20 w-20 overflow-hidden rounded-lg border bg-muted" data-testid={`composer-media-${m.id}`}>
            {m.kind === "video"
              ? <video src={m.url} className="h-full w-full object-cover" muted />
              : <img src={m.url} alt={m.filename} className="h-full w-full object-cover" />}
            {m.kind === "video" && <VideoCamera size={14} className="absolute bottom-1 left-1 text-white drop-shadow" weight="fill" />}
            <button type="button" onClick={() => setMedia(media.filter((x) => x.id !== m.id))}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white" data-testid={`composer-media-remove-${m.id}`}>
              <X size={12} />
            </button>
          </div>
        ))}
        <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-[11px] text-muted-foreground hover:bg-muted">
          <ImageSquare size={20} />
          {progress === null ? "Ajouter" : `${progress} %`}
          <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
            className="hidden" disabled={progress !== null} onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
            data-testid="composer-media-input" />
        </label>
      </div>
      <p className="text-[11px] text-muted-foreground">Jusqu'à 10 images, ou une seule vidéo (250 Mo max). Instagram et TikTok exigent un média.</p>
    </div>
  );
}

function TextCounters({ form }) {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
      {form.networks.map((id) => {
        const len = (form.texts[id] || form.text).length; const over = len > NETWORKS[id].limit;
        return <span key={id} className={over ? "text-red-600 font-semibold" : ""} data-testid={`composer-count-${id}`}>{NETWORKS[id].label} {len}/{NETWORKS[id].limit}</span>;
      })}
    </div>
  );
}

function Overrides({ form, setForm }) {
  const [open, setOpen] = useState(Object.keys(form.texts).length > 0);
  if (!form.networks.length) return null;
  return (
    <div>
      <button type="button" className="text-xs underline text-muted-foreground" onClick={() => setOpen(!open)} data-testid="composer-overrides-toggle">
        {open ? "Masquer les textes par réseau" : "Personnaliser le texte par réseau"}
      </button>
      {open && form.networks.map((id) => (
        <div key={id} className="mt-2">
          <Label className="text-xs">{NETWORKS[id].label} (vide = texte principal)</Label>
          <Textarea rows={3} value={form.texts[id] || ""} data-testid={`composer-text-${id}`}
            onChange={(e) => setForm({ ...form, texts: { ...form.texts, [id]: e.target.value } })} />
        </div>
      ))}
    </div>
  );
}

const TONES = ["professionnel", "convivial", "enthousiaste", "pédagogique"];

function AiWriter({ form, setForm, onDone }) {
  const [idea, setIdea] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!form.networks.length) { toast.error("Choisissez d'abord les réseaux"); return; }
    if (idea.trim().length < 3) { toast.error("Décrivez votre idée en une ligne"); return; }
    setBusy(true);
    try {
      const { job_id } = (await api.post("/social/ai/suggest", { idea, networks: form.networks, tone })).data;
      let job = { status: "running" };
      for (let i = 0; i < 60 && job.status === "running"; i += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        job = (await api.get(`/social/ai/suggest/${job_id}`)).data;
      }
      if (job.status !== "done") throw new Error(job.error || "L'IA n'a pas répondu à temps, réessayez.");
      const { texts } = job;
      const main = texts[form.networks[0]] || Object.values(texts)[0] || "";
      const overrides = Object.fromEntries(Object.entries(texts).filter(([, t]) => t !== main));
      setForm({ ...form, text: main, texts: overrides });
      onDone(Object.keys(overrides).length > 0);
      toast.success("Textes proposés — relisez-les avant d'envoyer");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-dashed p-3 space-y-2" data-testid="composer-ai">
      <Label className="flex items-center gap-1 text-xs"><Sparkle size={14} weight="fill" className="text-amber-500" />Rédaction par l'IA</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="Votre idée en une ligne, ex. : nouvelle offre de sauvegarde cloud pour les PME"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }} data-testid="composer-ai-idea" />
        <select className="rounded-md border bg-background px-2 py-2 text-sm" value={tone} onChange={(e) => setTone(e.target.value)} data-testid="composer-ai-tone">
          {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button type="button" variant="secondary" onClick={run} disabled={busy} data-testid="composer-ai-generate">
          <Sparkle size={14} className="mr-1" />{busy ? "Rédaction…" : "Proposer un texte"}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Un texte adapté à chaque réseau choisi. Le texte actuel sera remplacé.</p>
    </div>
  );
}

function WhenPicker({ form, setForm, isAdmin }) {
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Button type="button" size="sm" variant={form.when === "now" ? "default" : "outline"} onClick={() => setForm({ ...form, when: "now" })} data-testid="composer-when-now">
          <Lightning size={14} className="mr-1" />{isAdmin ? "Publier maintenant" : "Dès validation"}
        </Button>
        <Button type="button" size="sm" variant={form.when === "later" ? "default" : "outline"} onClick={() => setForm({ ...form, when: "later" })} data-testid="composer-when-later">
          <CalendarBlank size={14} className="mr-1" />Programmer
        </Button>
      </div>
      {form.when === "later" && (
        <div>
          <Input type="datetime-local" min={toLocalInput(new Date().toISOString())} value={form.scheduled} onChange={(e) => setForm({ ...form, scheduled: e.target.value })} className="max-w-xs" data-testid="composer-schedule-input" />
          <p className="text-[11px] text-muted-foreground mt-1">Le post part dans les 15 minutes qui suivent l'heure choisie.</p>
        </div>
      )}
    </div>
  );
}

export function PostComposer({ open, onClose, post, defaultDate, accounts, isAdmin, onSaved }) {
  const [form, setForm] = useState(empty);
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ovKey, setOvKey] = useState(0);

  useEffect(() => {
    if (!open) { return; }
    if (post) {
      setForm({ text: post.text || "", texts: post.texts || {}, networks: post.networks || [], when: post.scheduled_at && post.scheduled_at > new Date().toISOString() ? "later" : "now",
        scheduled: toLocalInput(post.scheduled_at), tiktok_privacy: post.tiktok_privacy || "PUBLIC_TO_EVERYONE" });
      setMedia(post.media || []);
    } else {
      setForm({ ...empty, when: defaultDate ? "later" : "now", scheduled: defaultDate ? toLocalInput(defaultDate) : "" });
      setMedia([]);
    }
  }, [open, post, defaultDate]);

  const save = async (action) => {
    if (action === "submit" && form.when === "later" && !form.scheduled) { toast.error("Choisissez une date"); return; }
    if (action === "submit" && form.when === "later" && new Date(form.scheduled) < new Date()) { toast.error("La date choisie est déjà passée"); return; }
    setBusy(true);
    const body = { text: form.text, texts: form.texts, networks: form.networks, media_ids: media.map((m) => m.id),
      scheduled_at: form.when === "later" && form.scheduled ? new Date(form.scheduled).toISOString() : null,
      tiktok_privacy: form.tiktok_privacy, action };
    try {
      const r = post ? await api.put(`/social/posts/${post.id}`, body) : await api.post("/social/posts", body);
      toast.success(action === "draft" ? "Brouillon enregistré" : r.data.status === "pending_approval" ? "Envoyé pour validation" : form.when === "now" ? "Publication lancée" : "Post programmé");
      onSaved?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitLabel = !isAdmin ? "Envoyer pour validation" : form.when === "now" ? "Publier" : "Programmer";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl lg:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="post-composer">
        <DialogHeader>
          <DialogTitle>{post ? "Modifier le post" : "Nouveau post"}</DialogTitle>
          <DialogDescription>Rédigez une fois, publiez sur les réseaux choisis.</DialogDescription>
        </DialogHeader>
        {post?.rejection_reason && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="composer-rejection">Refusé : {post.rejection_reason}</div>}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5 min-w-0">
          <div className="space-y-2"><Label>Réseaux</Label><NetworkPicker value={form.networks} onChange={(networks) => setForm({ ...form, networks })} accounts={accounts} /></div>
          <div className="space-y-2">
            <AiWriter form={form} setForm={setForm} onDone={() => setOvKey((k) => k + 1)} />
            <Label>Texte</Label>
            <Textarea rows={6} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Votre message…" data-testid="composer-text" />
            <TextCounters form={form} />
            <Overrides key={ovKey} form={form} setForm={setForm} />
          </div>
          <div className="space-y-2"><Label>Images / vidéo</Label><MediaPicker media={media} setMedia={setMedia} /></div>
          {form.networks.includes("tiktok") && (
            <div className="space-y-1">
              <Label>Visibilité TikTok</Label>
              <select className="block rounded-md border bg-background px-3 py-2 text-sm" value={form.tiktok_privacy}
                onChange={(e) => setForm({ ...form, tiktok_privacy: e.target.value })} data-testid="composer-tiktok-privacy">
                {TIKTOK_PRIVACY.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-2"><Label>Quand</Label><WhenPicker form={form} setForm={setForm} isAdmin={isAdmin} /></div>
        </div>
        <aside className="lg:sticky lg:top-0 lg:self-start space-y-2">
          <Label>Aperçu</Label>
          <PostPreview form={form} media={media} accounts={accounts} />
        </aside>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="composer-cancel">Annuler</Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={busy} data-testid="composer-save-draft">Enregistrer le brouillon</Button>
          <Button onClick={() => save("submit")} disabled={busy} data-testid="composer-submit">{busy ? "…" : submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
